/**
 * Berechnet den Skalierungsfaktor einer Sonne basierend auf ihrem
 * Lifecycle-Zustand. Wird von SunLifecycle pro Frame aufgerufen.
 *
 * Phasen:
 *   ALIVE:      1.0×
 *   IMPLODING:  1.0× → 1.3× (Roter Riese — Hülle expandiert)
 *   EXPLODING:  Flash(1.5×) → schneller Zerfall → 0×
 *   COLLAPSING: 1.3× → 0× schlagartig (~0.5s)
 *   DEAD/BH:    0×
 */

import { SunState } from './SunLifecycleTypes';
import type { SunLifecycleConfig } from '../config/SunLifecycleConfig';

const MAX_IMPLODE_SCALE = 1.3;
const FLASH_PEAK = 1.5;

/** Berechnet scaleMul für den aktuellen Zustand und verbleibende Zeit. */
export function calcLifecycleScale(
  state: SunState,
  remainingSec: number,
  config: SunLifecycleConfig,
): number {
  switch (state) {
    case SunState.Alive:
      return 1.0;

    case SunState.Imploding: {
      const range = config.implodeThresholdSec - config.deathThresholdSec;
      const t = 1.0 - (remainingSec - config.deathThresholdSec) / range;
      const clamped = Math.max(0, Math.min(1, t));
      const eased = clamped * clamped * (3 - 2 * clamped);
      return 1.0 + eased * (MAX_IMPLODE_SCALE - 1.0);
    }

    case SunState.Exploding:
      return calcExplosionScale(remainingSec, config);

    case SunState.CollapsingToBlackHole:
      return calcImplodeCollapse(remainingSec, config);

    case SunState.BlackHole:
    case SunState.Dead:
      return 0.0;
  }
}

/** Explosion: Kurzer Flash, dann schnelles "Pop" — Kugel platzt. */
function calcExplosionScale(remainingSec: number, config: SunLifecycleConfig): number {
  const t = 1.0 - remainingSec / config.deathThresholdSec;
  const c = Math.max(0, Math.min(1, t));

  // Flash — kurze Expansion (0–10%, ~0.3s)
  if (c < 0.10) {
    const p = c / 0.10;
    const ease = p * p;
    return MAX_IMPLODE_SCALE + ease * (FLASH_PEAK - MAX_IMPLODE_SCALE);
  }
  // Pop — Kugel platzt schnell weg (10–30%, ~0.6s)
  if (c < 0.30) {
    const p = (c - 0.10) / 0.20;
    const ease = p * p;
    return FLASH_PEAK * (1.0 - ease);
  }
  // Weg — Debris übernimmt
  return 0.0;
}

/** Schwarzes Loch: Schlagartiges Einsaugen zum Zentrum. */
function calcImplodeCollapse(remainingSec: number, config: SunLifecycleConfig): number {
  const t = 1.0 - remainingSec / config.deathThresholdSec;
  const c = Math.max(0, Math.min(1, t));

  // Kurzer Moment (~0.24s) — Sonne friert kurz ein
  if (c < 0.08) return MAX_IMPLODE_SCALE;
  // Schlagartiger Kollaps (~0.51s) — beschleunigtes Schrumpfen
  if (c < 0.25) {
    const p = (c - 0.08) / 0.17;
    const ease = p * p * p;
    return MAX_IMPLODE_SCALE * (1.0 - ease);
  }
  // Verschwunden
  return 0.0;
}
